import type { FetchPageServiceParameters } from "~/core/composables/useFetchPage";
import type { GenericParameters } from "~/core/models/GenericParameters.type";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import RestService from "~/core/services/rest.service";
import businessUnitService from "~/modules/businessUnit/services/businessUnit.service";
import organizationService from "~/modules/organization/services/organization.service";
import type { CreateUserDelegationParams } from "~/modules/relation/delegation/models/CreateUserDelegationParams.type";
import type { DelegationDetails } from "~/modules/relation/delegation/models/DelegationDetails.interface";
import type { UserDelegation } from "~/modules/relation/delegation/models/UserDelegation.interface";
import type { CreateRelationByUploadingIdCardParams } from "~/modules/relation/models/CreateRelationByUploadingIdCardParams.type";
import type { RelationEntity } from "~/modules/relation/models/RelationEntity.interface";
import relationService from "~/modules/relation/service/relation.service";

export class DelegationService extends RestService {
  protected DELEGATION_LINKS = "delegationLinks";
  protected USERS = "users";
  protected RECEIVED = "received";
  protected DELEGATIONS = "delegations";

  createOrganizationDelegationByUploadingIdCard({
    upload,
    organizationCode,
  }: CreateRelationByUploadingIdCardParams): Promise<void> {
    return this.$apiV2.post(
      `${organizationService.getOrganizationPath(organizationCode)}/${this.getRelationsDelegationPath()}`,
      relationService.createUploadFormData(upload.file),
      relationService.createUploadConfig(upload.progress),
    );
  }

  getOrganizationGivenDelegations({
    organizationCode,
    page,
    pageSize,
    searchParams,
  }: FetchPageServiceParameters): Promise<PaginatedList<RelationEntity>> {
    const params = {
      page,
      pageSize,
      filter: searchParams,
    };
    return this.$apiV2.get(
      `${organizationService.getOrganizationPath(organizationCode)}/${this.getRelationsDelegationPath()}/given`,
      {
        params,
      },
    );
  }

  getBusinessUnitReceivedDelegations({
    businessUnitCode,
    page,
    pageSize,
    searchParams,
  }: FetchPageServiceParameters): Promise<PaginatedList<RelationEntity>> {
    const params = {
      page,
      pageSize,
      filter: searchParams,
    };
    return this.$apiV2.get(
      `${businessUnitService.getBusinessUnitPath(businessUnitCode)}/${this.getRelationsDelegationPath()}/${this.RECEIVED}`,
      {
        params,
      },
    );
  }

  getOrganizationReceivedDelegations({
    organizationCode,
    page,
    pageSize,
    searchParams,
  }: FetchPageServiceParameters): Promise<PaginatedList<RelationEntity>> {
    const params = {
      page,
      pageSize,
      filter: searchParams,
    };
    return this.$apiV2.get(
      `${organizationService.getOrganizationPath(organizationCode)}/${this.getRelationsDelegationPath()}/${this.RECEIVED}`,
      {
        params,
      },
    );
  }

  getBusinessUnitUserDelegations({
    businessUnitCode,
    page,
    pageSize,
    searchParams,
    params,
  }: FetchPageServiceParameters): Promise<PaginatedList<UserDelegation>> {
    const fetchParams = {
      page,
      pageSize,
      filter: searchParams,
    };
    return this.$apiV2.get(
      `${businessUnitService.getBusinessUnitPath(businessUnitCode)}/${this.getUserDelegationPath(params?.get("userLogin") as string)}`,
      {
        params: fetchParams,
      },
    );
  }

  getOrganizationUserDelegations({
    organizationCode,
    page,
    pageSize,
    searchParams,
    params,
  }: FetchPageServiceParameters): Promise<PaginatedList<UserDelegation>> {
    const fetchParams = {
      page,
      pageSize,
      filter: searchParams,
    };
    return this.$apiV2.get(
      `${organizationService.getOrganizationPath(organizationCode)}/${this.getUserDelegationPath(params?.get("userLogin") as string)}`,
      {
        params: fetchParams,
      },
    );
  }

  getOrganizationDelegationDetails(
    targetOrganizationCode: number,
    sourceOrganizationCode?: number,
  ): Promise<DelegationDetails> {
    return this.$apiV2.get(
      `${organizationService.getOrganizationPath(sourceOrganizationCode)}/${this.getRelationsDelegationPath()}/${targetOrganizationCode}`,
    );
  }

  createBusinessUnitUserDelegation({
    businessUnitCode,
    userLogin,
    payload,
  }: CreateUserDelegationParams): Promise<UserDelegation> {
    return this.$apiV2.post(
      `${businessUnitService.getBusinessUnitPath(businessUnitCode)}/${this.getUserDelegationPath(userLogin)}`,
      payload,
    );
  }

  createOrganizationUserDelegation({
    organizationCode,
    userLogin,
    payload,
  }: CreateUserDelegationParams): Promise<UserDelegation> {
    return this.$apiV2.post(
      `${organizationService.getOrganizationPath(organizationCode)}/${this.getUserDelegationPath(userLogin)}`,
      payload,
    );
  }

  deleteOrganizationGivenDelegation({
    organizationCode,
    targetOrganizationCode,
  }: GenericParameters & { targetOrganizationCode: number }): Promise<void> {
    return this.$apiV2.delete(
      `${organizationService.getOrganizationPath(organizationCode)}/${this.getRelationsDelegationPath()}/${targetOrganizationCode}`,
    );
  }

  getRelationsDelegationPath(): string {
    return `${relationService.getRelationsPath()}/${this.DELEGATION_LINKS}`;
  }

  getUserDelegationPath(userLogin?: string): string {
    if (!userLogin) {
      throw new Error("User login is required for delegation path");
    }
    return `${relationService.getRelationsPath()}/${this.USERS}/${userLogin}/${this.DELEGATIONS}`;
  }
}

export default new DelegationService();
